import { V2 } from '../../theme/tokens'
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  LinearProgress,
  Button,
  Paper,
} from '@mui/material';
import {
  ExpandMore,
  OpenInNew,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Info as InfoIcon,
  AccountTree as RelationsIcon,
  BugReport as QualityIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { formatDateTime, formatPercent } from '../../utils/format';
import CardListSkeleton from '../../Skeletons/CardListSkeleton';
import { v2Tone } from '../../theme/theme';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const READINESS_COLOR = (score) => {
  if (score >= 80) return V2.okText;
  if (score >= 50) return V2.warnText;
  return V2.errText;
};

// 🔴 K-6 (UI2 Teil 8, 2026-10-10) — die vier literalen Kontraststellen dieser
// Datei, gemessen gegen den TATSAECHLICHEN Grund (Theme-Werte des Baums, hell
// und dunkel), NICHT gegen das Papier:
//
//   * Symbol im `Alert` (Nicht-Text, Boden 3:1) — der Grund ist die getoente
//     `Alert`-Flaeche, nicht das Papier. Das `warning`-Symbol lag dort hell bei
//     2,864:1 (mit der `AlertIcon`-Deckkraft 0,9: 2,588:1), also UNTER dem
//     Boden; das `info`-Symbol bei 3,477:1 (3,066:1), knapp darueber.
//   * Gefuellter `Chip` (Text auf Fuellung, Boden 4,5:1) — die MUI-Paarung
//     `palette.<ton>.main` + `contrastText` schreibt weisse Schrift und misst
//     hell 3,113:1 (`warning`) bzw. 3,857:1 (`info`), beide unter dem Boden.
//
// Hausform fuer beide ist die v2-Tonskala, die in beiden Modi traegt — dieselbe
// Paarung wie `POInbox.jsx:718` (`bgcolor` aus `v2Tone`, Schrift
// modusabhaengig). Danach: Symbol 4,620:1 / 3,910:1 (`warn`) und
// 4,660:1 / 3,946:1 (`info`); Chip 5,021:1 / 5,170:1 hell und
// 10,690:1 / 7,020:1 dunkel. Gemessen in
// `ProductIntelligenceTab.kontrast.test.jsx` (beide Modi, mit Rotbeweis).
const v2ToneSx = (ton) => (th) => ({ color: v2Tone(th, ton).main });

// Der gefuellte Chip: Grund aus der v2-Skala, Schrift modusabhaengig — hell
// traegt der Grund weisse Schrift, dunkel ist er hell genug fuer die dunkle.
export const v2ChipSx = (ton) => (th) => ({
  bgcolor: v2Tone(th, ton).main,
  color: th.palette.mode === 'dark' ? th.palette.background.default : th.palette.common.white,
});

export const SEVERITY_ICON = {
  critical: <ErrorIcon fontSize="small" color="error" />,
  warning: <Warning fontSize="small" sx={v2ToneSx('warn')} />,
  info: <InfoIcon fontSize="small" sx={v2ToneSx('info')} />,
};

const SEVERITY_COLOR = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
};

function FieldRow({ label, value, overridden }) {
  if (value === null || value === undefined || value === '') return null;

  const displayValue = Array.isArray(value)
    ? value.length > 0 ? value.join(', ') : null
    : String(value);

  if (!displayValue) return null;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.75 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 160, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Typography variant="body2" fontWeight={overridden ? 600 : 400}>
          {displayValue}
        </Typography>
        {overridden && (
          <Chip label="override" size="small" color="primary" variant="outlined" sx={{ height: 16, fontSize: 9 }} />
        )}
      </Box>
    </Box>
  );
}

function ReadinessGauge({ score }) {
  const color = READINESS_COLOR(score);
  const label = score >= 80 ? 'Ready' : score >= 50 ? 'Needs Work' : 'Incomplete';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={score}
          size={72}
          thickness={5}
          sx={{ color }}
        />
        <Box sx={{
          top: 0, left: 0, bottom: 0, right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Typography variant="body2" fontWeight="bold" color={color}>
            {score}%
          </Typography>
        </Box>
      </Box>
      <Chip
        label={label}
        size="small"
        sx={{ bgcolor: `${color}15`, color, fontWeight: 600, border: 'none' }}
      />
    </Box>
  );
}

function RelationCountBar({ label, count, total, color }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" fontWeight="600">{count}</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, pct)}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: 'rgba(0,0,0,0.06)',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
        }}
      />
    </Box>
  );
}

function SignalBadge({ signal }) {
  const weightColor = signal.weight >= 2 ? V2.okText : V2.ink70;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
      <Chip
        label={`${signal.source} = '${signal.value}'`}
        size="small"
        variant="outlined"
        sx={{ fontSize: 10, height: 20 }}
      />
      <Typography variant="caption" sx={{ color: weightColor, fontWeight: 600 }}>
        Weight: +{signal.weight}
      </Typography>
    </Box>
  );
}

export default function ProductIntelligenceTab() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load product list on mount
  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/products`)
      .then((res) => setProducts(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
      .finally(() => setProductsLoading(false));
  }, []);

  const fetchIntelligence = useCallback(async (product) => {
    setLoading(true);
    setError(null);
    setIntel(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/taxonomy/product-intelligence/${product.product_id}`);
      setIntel(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load product intelligence');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelect = (_, newValue) => {
    setSelected(newValue);
    if (newValue) {
      fetchIntelligence(newValue);
    } else {
      setIntel(null);
    }
  };

  const overrides = intel?.overrides || {};

  const effectiveValue = (field) => {
    if (overrides[field] !== undefined && overrides[field] !== null) {
      return { value: overrides[field], overridden: true };
    }
    return { value: intel?.taxonomy?.[field], overridden: false };
  };

  const issueCounts = intel
    ? {
        critical: intel.issues.filter((i) => i.severity === 'critical').length,
        warning: intel.issues.filter((i) => i.severity === 'warning').length,
        info: intel.issues.filter((i) => i.severity === 'info').length,
      }
    : null;

  return (
    <Box sx={{ py: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Product Intelligence
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Aggregated taxonomy, relations, and recommendation readiness for a single product.
        </Typography>
      </Box>

      {/* Product selector */}
      <Autocomplete
        options={products}
        getOptionLabel={(o) => `${o.product_name} (${o.sku})`}
        filterOptions={(opts, state) => {
          const q = state.inputValue.toLowerCase();
          if (!q) return opts.slice(0, 100);
          return opts.filter(
            (o) => o.product_name?.toLowerCase().includes(q) || o.sku?.toLowerCase().includes(q)
          ).slice(0, 100);
        }}
        value={selected}
        onChange={handleSelect}
        loading={productsLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Product"
            placeholder="Search by name or SKU…"
            variant="outlined"
          />
        )}
        sx={{ maxWidth: 600, mb: 3 }}
      />

      {loading && (
        // K-S (UI-Durchgang 2): Geruest der geladenen Intelligenz-Karten
        // statt Kreisel.
        // 🔴 K-6 (UI2 Teil 8, 2026-10-10): geladen steht hier eine
        // `Card variant="outlined"` (`:336`) bzw. eine `Paper variant="outlined"`
        // (`:310`) — Radius 6 px, KEIN Schatten (gemessen). Die Vorgabeform
        // `panel` traegt 18 px und einen Panel-Schatten; der Rahmen sprang beim
        // Eintreffen der Daten. `form="outlined"` trifft Radius UND Schatten.
        <CardListSkeleton
          cards={4}
          lines={3}
          form="outlined"
          ariaLabel="Loading product intelligence"
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2, maxWidth: 600 }}>
          {error}
        </Alert>
      )}

      {!loading && !selected && !error && (
        <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
          <Typography variant="h6">Select a product to view its intelligence profile</Typography>
        </Box>
      )}

      {!loading && intel && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* ── Header ── */}
          <Paper
            variant="outlined"
            sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}
          >
            <Box>
              <Typography variant="h5" gutterBottom>
                {intel.product.product_name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`SKU: ${intel.product.sku || '—'}`} size="small" variant="outlined" />
                {intel.product.vendor && (
                  <Chip label={`Vendor: ${intel.product.vendor}`} size="small" variant="outlined" />
                )}
                {intel.has_override && (
                  <Chip label="Has overrides" size="small" color="primary" variant="outlined" />
                )}
                {!intel.has_taxonomy && (
                  <Chip label="No taxonomy data" size="small" color="error" />
                )}
              </Box>
            </Box>
            <ReadinessGauge score={intel.readiness_score} />
          </Paper>

          {/* ── Issues ── */}
          {intel.issues.length > 0 && (
            <Card variant="outlined">
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <QualityIcon fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={600}>Quality Issues</Typography>
                    {issueCounts.critical > 0 && (
                      <Chip label={`${issueCounts.critical} critical`} size="small" color="error" />
                    )}
                    {issueCounts.warning > 0 && (
                      <Chip label={`${issueCounts.warning} warning`} size="small" sx={v2ChipSx('warn')} />
                    )}
                    {issueCounts.info > 0 && (
                      <Chip label={`${issueCounts.info} info`} size="small" sx={v2ChipSx('info')} />
                    )}
                  </Box>
                }
                action={
                  <Button
                    size="small"
                    startIcon={<OpenInNew />}
                    onClick={() => navigate('/taxonomy?tab=quality')}
                  >
                    Quality Inspector
                  </Button>
                }
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {intel.issues.map((issue, idx) => (
                    <Alert
                      key={idx}
                      severity={SEVERITY_COLOR[issue.severity] || 'info'}
                      icon={SEVERITY_ICON[issue.severity]}
                      sx={{ py: 0.5 }}
                    >
                      <Typography variant="body2">{issue.message}</Typography>
                      {issue.field && (
                        <Typography variant="caption" color="text.secondary">
                          Field: <strong>{issue.field}</strong>
                        </Typography>
                      )}
                    </Alert>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* ── Relations ── */}
          <Card variant="outlined">
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RelationsIcon fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>Relations</Typography>
                </Box>
              }
              action={
                <Button
                  size="small"
                  startIcon={<OpenInNew />}
                  onClick={() => navigate('/taxonomy?tab=relations')}
                >
                  Relations Manager
                </Button>
              }
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <RelationCountBar
                      label="Complements"
                      count={intel.relations.counts.complement}
                      total={intel.relations.counts.total}
                      color={V2.okText}
                    />
                    <RelationCountBar
                      label="Add-ons"
                      count={intel.relations.counts.addon}
                      total={intel.relations.counts.total}
                      color={V2.infoText}
                    />
                    <RelationCountBar
                      label="Competitors"
                      count={intel.relations.counts.competitor}
                      total={intel.relations.counts.total}
                      color={V2.warnText}
                    />
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Total</Typography>
                      <Typography variant="body2" fontWeight={600}>{intel.relations.counts.total}</Typography>
                    </Box>
                  </Box>
                </Grid>
                {['complement', 'addon', 'competitor'].map((rtype) => {
                  const items = intel.relations.top[rtype] || [];
                  const colorMap = { complement: V2.okText, addon: V2.infoText, competitor: V2.warnText };
                  const labelMap = { complement: 'Top Complements', addon: 'Top Add-ons', competitor: 'Top Competitors' };
                  return (
                    <Grid size={{ xs: 12, sm: 8 / 3 }} key={rtype}>
                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                        {labelMap[rtype]}
                      </Typography>
                      {items.length === 0 ? (
                        <Typography variant="caption" color="text.disabled">None yet</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {items.map((rel, i) => (
                            <Box
                              key={i}
                              sx={{
                                p: 1,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Typography variant="caption" noWrap sx={{ maxWidth: '75%' }}>
                                {rel.name || rel.candidate_name || `Product ${rel.product_id || rel.candidate_product_id}`}
                              </Typography>
                              {rel.match_score != null && (
                                <Chip
                                  label={formatPercent(rel.match_score, { digits: 0 })}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: 9, color: colorMap[rtype], borderColor: colorMap[rtype] }}
                                />
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>

          {/* ── Core Taxonomy ── */}
          {intel.has_taxonomy && (
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle1" fontWeight={600}>Core Taxonomy</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    {[
                      ['Canonical Type', 'canonical_type'],
                      ['Display Name', 'canonical_type_display_name'],
                      ['Equipment Category', 'equipment_category'],
                      ['Skill Level', 'skill_level'],
                      ['Skill Level Required', 'skill_level_required'],
                      ['Brew Focus', 'brew_focus'],
                      ['Grind Range', 'grind_range'],
                      ['Maintenance Level', 'maintenance_level'],
                      ['Barista Tier', 'barista_tier'],
                      ['Form Factor', 'form_factor'],
                      ['Price Band', 'price_band'],
                      ['Role', 'role'],
                      ['Grinder Style', 'grinder_style'],
                    ].map(([label, field]) => {
                      const { value, overridden } = effectiveValue(field);
                      return <FieldRow key={field} label={label} value={value} overridden={overridden} />;
                    })}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    {[
                      ['Power Requirement', 'power_requirement'],
                      ['Build Quality', 'build_quality'],
                      ['Portability Score', 'portability_score'],
                      ['Portafilter Size', 'portafilter_size'],
                      ['Burr Diameter (mm)', 'burr_diameter_mm'],
                      ['Burr Geometry', 'burr_geometry'],
                      ['Burr Focus', 'burr_focus'],
                    ].map(([label, field]) => {
                      const { value, overridden } = effectiveValue(field);
                      return <FieldRow key={field} label={label} value={value} overridden={overridden} />;
                    })}
                    <Divider sx={{ my: 1 }} />
                    {[
                      ['Occasion Fit', 'occasion_fit'],
                      ['Brew Methods', 'brew_methods'],
                      ['System Memberships', 'system_memberships'],
                    ].map(([label, field]) => {
                      const { value, overridden } = effectiveValue(field);
                      return <FieldRow key={field} label={label} value={value} overridden={overridden} />;
                    })}
                  </Grid>
                </Grid>
                {(intel.taxonomy.espresso_focus_score != null || intel.taxonomy.filter_focus_score != null) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', gap: 4 }}>
                      {intel.taxonomy.espresso_focus_score != null && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Espresso Focus Score</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.round(intel.taxonomy.espresso_focus_score * 100)}
                              sx={{ width: 100, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="body2" fontWeight={600}>
                              {formatPercent(intel.taxonomy.espresso_focus_score, { digits: 0 })}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {intel.taxonomy.filter_focus_score != null && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Filter Focus Score</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.round(intel.taxonomy.filter_focus_score * 100)}
                              sx={{ width: 100, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: V2.infoText } }}
                            />
                            <Typography variant="body2" fontWeight={600}>
                              {formatPercent(intel.taxonomy.filter_focus_score, { digits: 0 })}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── AI Content Attributes ── */}
          {intel.has_taxonomy && (intel.taxonomy.ideal_for?.length > 0 || intel.taxonomy.feature_highlights?.length > 0 || intel.taxonomy.compatible_equipment?.length > 0) && (
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle1" fontWeight={600}>AI Content Attributes</Typography>}
                subheader={<Typography variant="caption" color="text.secondary">Written by the Taxonomy Agent — use context, highlights, and compatible gear</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Grid container spacing={2}>
                  {intel.taxonomy.ideal_for?.length > 0 && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Ideal For</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {intel.taxonomy.ideal_for.map((v) => (
                          <Chip key={v} label={v} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  {intel.taxonomy.feature_highlights?.length > 0 && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Feature Highlights</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {intel.taxonomy.feature_highlights.map((v) => (
                          <Chip key={v} label={v} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                  {intel.taxonomy.compatible_equipment?.length > 0 && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Compatible Equipment</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {intel.taxonomy.compatible_equipment.map((v) => (
                          <Chip key={v} label={v} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* ── Use Case Analysis ── */}
          {intel.has_taxonomy && (
            <Accordion variant="outlined" defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>Use Case Analysis</Typography>
                  {intel.use_case.computed && (
                    <Chip
                      label={intel.use_case.computed}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {Object.entries(intel.use_case.signals || {}).map(([useCase, sigs]) => {
                    if (sigs.length === 0) return null;
                    const isComputed = useCase === intel.use_case.computed;
                    return (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }} key={useCase}>
                        <Box
                          sx={{
                            p: 1.5,
                            border: 2,
                            borderColor: isComputed ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            bgcolor: isComputed ? 'primary.50' : 'transparent',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            {isComputed && <CheckCircle fontSize="small" color="primary" />}
                            <Typography variant="subtitle2" textTransform="capitalize">
                              {useCase}
                            </Typography>
                            <Chip
                              label={`${sigs.reduce((s, x) => s + x.weight, 0)} pts`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: 9 }}
                            />
                          </Box>
                          {sigs.map((sig, i) => <SignalBadge key={i} signal={sig} />)}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>

                {intel.use_case.blockers && intel.use_case.blockers.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Active Blockers
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {intel.use_case.blockers.map((b, i) => (
                        <Alert key={i} severity="warning" sx={{ py: 0.25 }}>
                          <Typography variant="caption">
                            <strong>{b.source}</strong> — {b.reason} (−{b.weight} pts)
                          </Typography>
                        </Alert>
                      ))}
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {/* ── Bundle & Persona ── */}
          {(() => {
            const { value: bundleSlots, overridden: bundleOverridden } = effectiveValue('bundle_slots');
            const { value: personaFit, overridden: personaOverridden } = effectiveValue('persona_fit');
            const hasBundleSlots = Array.isArray(bundleSlots) && bundleSlots.length > 0;
            const hasPersonaFit = personaFit && typeof personaFit === 'object' && Object.keys(personaFit).length > 0;
            if (!hasBundleSlots && !hasPersonaFit) return null;
            return (
              <Card variant="outlined">
                <CardHeader
                  title={<Typography variant="subtitle1" fontWeight={600}>Bundle &amp; Persona</Typography>}
                  sx={{ pb: 0 }}
                />
                <CardContent>
                  <Grid container spacing={3}>
                    {hasBundleSlots && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Bundle Slots</Typography>
                          {bundleOverridden && <Chip label="override" size="small" color="primary" variant="outlined" sx={{ height: 16, fontSize: 9 }} />}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {bundleSlots.map((slot) => (
                            <Chip key={slot} label={slot} size="small" color="success" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}
                    {hasPersonaFit && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">Persona Fit Scores</Typography>
                          {personaOverridden && <Chip label="override" size="small" color="primary" variant="outlined" sx={{ height: 16, fontSize: 9 }} />}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {Object.entries(personaFit).sort(([, a], [, b]) => b - a).map(([persona, score]) => (
                            <Box key={persona}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                <Typography variant="caption" color="text.secondary">{persona}</Typography>
                                <Typography variant="caption" fontWeight={600}>{formatPercent(score, { digits: 0 })}</Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={Math.round(score * 100)}
                                sx={{ height: 4, borderRadius: 2 }}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            );
          })()}

          {/* ── Data Source ── */}
          <Accordion variant="outlined">
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1" fontWeight={600}>Data Source &amp; Metadata</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {[
                    ['Source', intel.taxonomy.source],
                    ['Prompt Version', intel.taxonomy.prompt_version],
                    ['Last Updated', intel.taxonomy.updated_at
                      ? formatDateTime(intel.taxonomy.updated_at)
                      : null],
                  ].map(([label, value]) => value && (
                    <FieldRow key={label} label={label} value={value} />
                  ))}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldRow label="Has Taxonomy" value={intel.has_taxonomy ? 'Yes' : 'No'} />
                  <FieldRow label="Has Overrides" value={intel.has_override ? 'Yes' : 'No'} />
                  <FieldRow
                    label="Override Fields"
                    value={Object.keys(overrides).length > 0 ? Object.keys(overrides).join(', ') : null}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

        </Box>
      )}
    </Box>
  );
}
